import { useEffect, useRef, useState } from 'react';

import type { KubernetesConsoleApi } from './api';
import type { K8sObject, K8sWatchEvent } from './types';

const CHANGE_HIGHLIGHT_MS = 4_000;

export interface KubernetesWatchState {
  items: K8sObject[];
  connected: boolean;
  reconnecting: boolean;
  error: string | null;
  changed: ReadonlySet<string>;
}

function objectKey(object: K8sObject): string {
  return object.metadata.uid ?? object.metadata.name;
}

export function useKubernetesWatch(
  api: KubernetesConsoleApi,
  cluster: string,
  path: string | null,
): KubernetesWatchState {
  const resourceVersionRef = useRef<string>();
  const timersRef = useRef(new Map<string, number>());
  const [state, setState] = useState<KubernetesWatchState>({
    items: [],
    connected: false,
    reconnecting: false,
    error: null,
    changed: new Set(),
  });

  useEffect(() => {
    resourceVersionRef.current = undefined;
    for (const timer of timersRef.current.values()) window.clearTimeout(timer);
    timersRef.current.clear();
    if (!cluster || !path) {
      setState({
        items: [],
        connected: false,
        reconnecting: false,
        error: null,
        changed: new Set(),
      });
      return undefined;
    }

    const controller = new AbortController();
    const markChanged = (key: string) => {
      const existing = timersRef.current.get(key);
      if (existing) window.clearTimeout(existing);
      setState(current => ({
        ...current,
        changed: new Set(current.changed).add(key),
      }));
      timersRef.current.set(
        key,
        window.setTimeout(() => {
          timersRef.current.delete(key);
          setState(current => {
            const changed = new Set(current.changed);
            changed.delete(key);
            return { ...current, changed };
          });
        }, CHANGE_HIGHLIGHT_MS),
      );
    };
    const receive = (event: K8sWatchEvent) => {
      if ('resourceVersion' in event && event.resourceVersion) {
        resourceVersionRef.current = event.resourceVersion;
      }
      if (event.type === 'INITIAL') {
        setState({
          items: event.items,
          connected: true,
          reconnecting: false,
          error: null,
          changed: new Set(),
        });
        return;
      }
      if (
        event.type === 'ADDED' ||
        event.type === 'MODIFIED' ||
        event.type === 'DELETED'
      ) {
        const key = objectKey(event.object);
        setState(current => {
          const items = current.items.filter(item => objectKey(item) !== key);
          if (event.type !== 'DELETED') items.push(event.object);
          return {
            ...current,
            items,
            connected: true,
            reconnecting: false,
            error: null,
          };
        });
        markChanged(key);
        return;
      }
      if (event.type === 'RECONNECTING') {
        setState(current => ({
          ...current,
          connected: false,
          reconnecting: true,
          error: event.message ?? null,
        }));
      } else if (event.type === 'ERROR') {
        setState(current => ({
          ...current,
          connected: false,
          reconnecting: Boolean(event.recoverable),
          error: event.message,
        }));
      } else if (event.type === 'BOOKMARK') {
        setState(current => ({
          ...current,
          connected: true,
          reconnecting: false,
        }));
      }
    };

    api
      .watchResources(cluster, path, receive, controller.signal)
      .catch(error => {
        if (controller.signal.aborted) return;
        setState(current => ({
          ...current,
          connected: false,
          reconnecting: false,
          error: error instanceof Error ? error.message : String(error),
        }));
      });

    return () => {
      controller.abort();
      for (const timer of timersRef.current.values()) {
        window.clearTimeout(timer);
      }
      timersRef.current.clear();
    };
  }, [api, cluster, path]);

  return state;
}
